import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  X, Loader, History, RotateCcw, AlertTriangle, CheckCircle2, Inbox
} from 'lucide-react';

/** Les gestes, dits en français plutôt qu'avec le nom de la mutation. */
const LIBELLES = {
  METTRE_EN_PAUSE_ANNONCE: 'Annonces mises en pause',
  METTRE_EN_PAUSE_MOT_CLE: 'Mots-clés mis en pause',
  RETIRER_NEGATIF: 'Mot-clé négatif retiré',
  COUPER_RESEAU_DISPLAY: 'Réseau Display coupé',
  CIBLER_PRESENCE: 'Ciblage limité à la présence'
};

const ORIGINES = {
  RADAR: 'Radar',
  DIAGNOSTIC: 'Diagnostic',
  ANNULATION: 'Annulation'
};

/**
 * Ce que Kokpit a écrit dans les régies, et comment revenir en arrière.
 *
 * ## Pourquoi cet écran existe
 *
 * Les écritures étaient archivées depuis l'origine — `McpWriteLog` enregistre tous les chemins,
 * succès comme refus — mais **leur lecture était réservée aux administrateurs**, et sans filtre
 * d'utilisateur : la route d'admin rend tout le parc. Un utilisateur ne voyait donc rien, pas
 * même ses propres corrections.
 *
 * Or on ne peut pas demander à quelqu'un d'écrire dans le compte d'un annonceur sans lui montrer
 * ce qui a été écrit. C'est aussi ce qui rend le geste défendable : qui, quand, quoi, et comment
 * le défaire.
 *
 * Le périmètre est celui de l'organisation, comme le feed : deux membres d'une agence doivent
 * pouvoir se relire.
 */
const JournalCorrections = ({ token, accountId = null, onFermer, onAnnule }) => {
  const [ecritures, setEcritures] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [occupe, setOccupe] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true); setErreur(null);
    try {
      const url = accountId
        ? `/api/actions/journal?accountId=${encodeURIComponent(accountId)}`
        : '/api/actions/journal';
      const reponse = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const donnees = await reponse.json();
      if (!reponse.ok || !donnees.success) throw new Error(donnees.error || 'Échec');
      setEcritures(donnees.ecritures || []);
    } catch (e) { setErreur(e.message); } finally { setChargement(false); }
  }, [token, accountId]);

  useEffect(() => { charger(); }, [charger]);

  useEffect(() => {
    const auClavier = (e) => { if (e.key === 'Escape' && !occupe) onFermer(); };
    window.addEventListener('keydown', auClavier);
    return () => window.removeEventListener('keydown', auClavier);
  }, [onFermer, occupe]);

  const annuler = async (ecriture) => {
    setOccupe(ecriture.id); setErreur(null);
    try {
      const reponse = await fetch(`/api/actions/${ecriture.id}/annuler`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({})
      });
      const donnees = await reponse.json();
      if (!reponse.ok || !donnees.success) throw new Error(donnees.error || 'Échec');
      await charger();
      if (onAnnule) onAnnule();
    } catch (e) { setErreur(e.message); } finally { setOccupe(null); }
  };

  const quand = (iso) => new Date(iso).toLocaleString('fr-FR', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
  });

  return ReactDOM.createPortal(
    <div className="kcorr-fond" onClick={() => { if (!occupe) onFermer(); }} role="presentation">
      <div
        className="kcorr-boite kjourn-boite"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Corrections faites depuis Kokpit"
      >
        <header className="kcorr-tete">
          <div>
            <h2><History size={17} /> Corrections faites depuis Kokpit</h2>
            <p className="kcorr-sous-titre">
              {accountId
                ? 'Sur ce compte'
                : 'Sur tous les comptes de votre périmètre'} — vous et les membres de votre organisation.
            </p>
          </div>
          <button className="kcorr-fermer" onClick={onFermer} disabled={Boolean(occupe)} aria-label="Fermer">
            <X size={18} />
          </button>
        </header>

        <div className="kcorr-corps">
          {erreur && (
            <p className="kcorr-avis kcorr-avis-erreur">
              <AlertTriangle size={15} />
              {erreur}
            </p>
          )}

          {chargement ? (
            <p className="kjourn-vide"><Loader size={22} className="kcorr-tourne" /> Chargement…</p>
          ) : ecritures.length === 0 ? (
            <p className="kjourn-vide">
              <Inbox size={26} />
              Aucune correction n'a encore été écrite depuis Kokpit.
            </p>
          ) : (
            <ul className="kjourn-liste">
              {ecritures.map(e => (
                <li key={e.id} className={e.succes ? '' : 'kjourn-echec'}>
                  <div className="kjourn-ligne">
                    <span className="kjourn-geste">
                      {e.succes ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
                      {/* L'annulation d'un geste se lit comme telle, et non comme un geste
                          de plus : sinon l'historique paraît avoir corrigé deux fois. */}
                      {e.origine === 'ANNULATION' ? 'Annulation — ' : ''}
                      {LIBELLES[e.action] || e.operation}
                    </span>
                    <span className="kjourn-quand">{quand(e.faitLe)}</span>
                  </div>

                  <div className="kjourn-detail">
                    <span>{e.compte}</span>
                    {e.controle && <span>· {e.controle}</span>}
                    <span>
                      · {e.reussis}/{e.elements} élément{e.elements > 1 ? 's' : ''}
                    </span>
                    {e.origine && e.origine !== 'ANNULATION' && <span>· depuis {ORIGINES[e.origine] || e.origine}</span>}
                    {e.auteur && <span>· {e.auteur}</span>}
                  </div>

                  {e.erreur && <p className="kjourn-erreur">{e.erreur}</p>}

                  {e.annulable && (
                    <button
                      className="kcorr-bouton kjourn-annuler"
                      onClick={() => annuler(e)}
                      disabled={Boolean(occupe)}
                    >
                      {occupe === e.id
                        ? <Loader size={14} className="kcorr-tourne" />
                        : <RotateCcw size={14} />}
                      Annuler
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <footer className="kcorr-pied">
          <button className="kcorr-bouton kcorr-bouton-fort" onClick={onFermer}>Fermer</button>
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default JournalCorrections;
